import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { sanitize } from 'webapp/utils/html';
import { getFormattedTime } from 'webapp/utils/number';

import safeStyle from 'webapp/helpers/safe-style';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type ReportSlide from 'webapp/models-custom/report-slide';

interface Args {
	model: ReportSlide;
	isOpen: boolean;
	isExpanded: boolean;
	toggleOpen: () => void;
	toggleExpand: () => void;
}

export default class ReportsViewerDescriptionComponent extends Component<Args> {
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	@tracked hasContentScroller = false;

	private contentNode: HTMLElement | null = null;
	private repositionFn: (() => void) | null = null;

	get content(): string {
		return this.args.model.description.content || '';
	}

	get sanitizedContent(): string {
		return sanitize(this.content);
	}

	get modifiedText(): string {
		let text = '';

		const ts = this.args.model.description.modified;
		if (ts) {
			text = getFormattedTime(ts, 'UTC', 'MMM D, YYYY [at] hh:mm A');
			text += ' (UTC)';
		}
		return text;
	}

	get showExpandToggle(): boolean {
		return (
			this.args.isOpen &&
			(this.hasContentScroller || // can only expand when have scroller
				this.args.isExpanded) // always allow collapsing if expanded
		);
	}

	@action
	setupView(node: HTMLElement): void {
		// only set contentNode (and by extension, run checkContentScroller and setDescriptionNodeHeight)
		// when this is in the preview popup or report datawall but only live version (not snapshooter)

		const modal = document.querySelector('.popup-type-board-preview'),
			liveReportDatawall = document.querySelector('.live-report');

		if (!modal && !liveReportDatawall) {
			return;
		}

		const contentNode = node.getElementsByClassName('content')[0];
		if (contentNode) {
			this.contentNode = contentNode as HTMLElement;
		}
		this.checkContentScroller();

		// bind events on resize (when description ct size changes)
		const repositionFn = () => {
			schedule('afterRender', () => {
				this.checkContentScroller();
				this.setDescriptionNodeHeight();
			});
		};
		this.repositionFn = repositionFn;

		window.addEventListener('resize', this.repositionFn);
	}

	@action
	teardownView(): void {
		const repositionFn = this.repositionFn;
		if (repositionFn) {
			window.removeEventListener('resize', repositionFn);
		}
	}

	@action
	checkContentScroller(): void {
		const contentNode = this.contentNode;
		if (contentNode) {
			this.hasContentScroller = contentNode.scrollHeight > contentNode.offsetHeight;
		}
	}

	@action
	toggleExpand(): void {
		this.args.toggleExpand();
	}

	@action
	setDescriptionNodeHeight(): void {
		const contentNode = this.contentNode;
		if (contentNode) {
			const slideDescNode = contentNode.closest('.report_slide_desc'),
				slideInnerAndDescNode = contentNode.closest('.report_slide-and-desc_ct');

			if (slideDescNode && slideInnerAndDescNode) {
				let heightValue;
				if (this.args.isExpanded) {
					const height = Math.min(
						slideInnerAndDescNode.clientHeight / 2,
						contentNode.scrollHeight,
					);
					heightValue = `${height}px`;
				} else {
					heightValue = '';
				}
				(slideDescNode as HTMLElement).style.height = heightValue;
			}
		}
	}

	@action
	toggleOpen(): void {
		this.args.toggleOpen();
		schedule('afterRender', this, this.checkContentScroller);
		this.mmReportsService.triggerInnerSlideResize();
	}
	<template>
		{{#if this.content}}
			<div
				class="report_slide_desc {{unless @isOpen 'closed'}} {{if @isExpanded 'expanded'}}"
				{{didInsert this.setupView}}
				{{didUpdate this.setDescriptionNodeHeight @isExpanded}}
				{{didUpdate this.checkContentScroller @isExpanded this.content}}
				{{willDestroy this.teardownView}}
			>
				<span
					class="toggle-handle"
					role="button"
					style={{safeStyle @model.shownColor property="background-color"}}
					{{on "click" this.toggleOpen}}
				>
					<CommonUiIconComponent @name="ChevronDown" @size="xs" />
				</span>
				<div class="content">
					{{{this.sanitizedContent}}}
					<div class="author">
						<AvatarOrInitialsComponent
							@name={{@model.description.userName}}
							@avatar={{@model.description.userAvatar}}
						/>
						<span>
							Note by
							<em>{{@model.description.userName}}</em>,
							{{this.modifiedText}}
						</span>
					</div>
				</div>
				{{#if this.showExpandToggle}}
					<a class="expand" {{on "click" this.toggleExpand}}>
						Click to
						{{#if @isExpanded}}collapse{{else}}expand{{/if}}
					</a>
				{{/if}}
			</div>
		{{/if}}
	</template>
}
